// 9 soru tipi canlı preview'u — danışmanın göreceği şekilde render.
// State yok — sadece görsel. M4'te renderer bileşeni buradan türetilebilir.

import type { Question } from "@mahir/shared";

const fieldStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid var(--color-border)",
  background: "var(--color-bg)",
  fontSize: "0.9rem",
  fontFamily: "var(--font-sans)",
  width: "100%",
};

export function QuestionPreview({ question }: { question: Question }): JSX.Element {
  const o = question.options;
  return (
    <div>
      <div style={{ marginBottom: 6, display: "flex", gap: 8, alignItems: "baseline" }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.7rem",
            color: "var(--color-text-3)",
          }}
        >
          {question.code}
        </span>
        {question.required ? (
          <span
            style={{
              fontSize: "0.65rem",
              color: "var(--color-error)",
              fontWeight: 600,
            }}
          >
            ZORUNLU
          </span>
        ) : null}
      </div>
      <label
        style={{
          display: "block",
          fontWeight: 500,
          marginBottom: 6,
          color: "var(--color-text)",
        }}
      >
        {question.label}
      </label>
      {question.description ? (
        <p style={{ color: "var(--color-text-2)", fontSize: "0.85rem", marginBottom: 8 }}>
          {question.description}
        </p>
      ) : null}
      {renderField(o)}
      {question.helperText ? (
        <p
          style={{
            color: "var(--color-text-3)",
            fontSize: "0.75rem",
            marginTop: 6,
            fontStyle: "italic",
          }}
        >
          {question.helperText}
        </p>
      ) : null}
      {question.allowNote ? (
        <details style={{ marginTop: 8 }}>
          <summary
            style={{
              fontSize: "0.75rem",
              color: "var(--color-primary)",
              cursor: "pointer",
            }}
          >
            + Danışman notu ekle
          </summary>
          <textarea placeholder="Not" rows={2} style={{ ...fieldStyle, marginTop: 6 }} />
        </details>
      ) : null}
      {question.allowEvidence ? (
        <div style={{ marginTop: 8, fontSize: "0.75rem", color: "var(--color-text-3)" }}>
          📎 Kanıt belgesi yüklenebilir
        </div>
      ) : null}
      {question.priorYearHintDisabled ? (
        <div
          style={{
            marginTop: 8,
            padding: "6px 10px",
            background: "color-mix(in srgb, var(--color-warning) 15%, white)",
            borderRadius: 4,
            fontSize: "0.75rem",
            color: "var(--color-text-2)",
          }}
        >
          🚫 Önceki yıl önerisi bu soruda gizli
        </div>
      ) : null}
    </div>
  );
}

function renderField(o: Question["options"]): JSX.Element {
  switch (o.type) {
    case "text":
      return (
        <textarea
          rows={3}
          placeholder={`En fazla ${o.maxLength} karakter`}
          style={fieldStyle}
          readOnly
        />
      );
    case "numeric":
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="number" style={{ ...fieldStyle, width: 180 }} placeholder="0" readOnly />
          {o.unit ? (
            <span style={{ color: "var(--color-text-3)", fontSize: "0.85rem" }}>{o.unit}</span>
          ) : null}
        </div>
      );
    case "single":
      return (
        <div style={{ display: "grid", gap: 6 }}>
          {o.choices.map((c) => (
            <div key={c.id}>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="radio" name="preview-single" disabled /> {c.label}
                {c.allowCustomInput && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.65rem",
                      color: "var(--color-text-3)",
                    }}
                  >
                    açıklayınız
                  </span>
                )}
              </label>
              {c.allowCustomInput && (
                <input
                  type="text"
                  placeholder={`${c.label} — açıklama…`}
                  disabled
                  style={{
                    ...fieldStyle,
                    width: "calc(100% - 1.5rem)",
                    marginLeft: 24,
                    marginTop: 4,
                  }}
                />
              )}
            </div>
          ))}
        </div>
      );
    case "multi":
      return (
        <div style={{ display: "grid", gap: 6 }}>
          {o.choices.map((c) => (
            <div key={c.id}>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" disabled /> {c.label}
                {c.allowCustomInput && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.65rem",
                      color: "var(--color-text-3)",
                    }}
                  >
                    açıklayınız
                  </span>
                )}
              </label>
              {c.allowCustomInput && (
                <input
                  type="text"
                  placeholder={`${c.label} — açıklama…`}
                  disabled
                  style={{
                    ...fieldStyle,
                    width: "calc(100% - 1.5rem)",
                    marginLeft: 24,
                    marginTop: 4,
                  }}
                />
              )}
            </div>
          ))}
        </div>
      );
    case "date":
      return (
        <input
          type={o.format === "MM-YYYY" ? "month" : "date"}
          style={{ ...fieldStyle, width: 200 }}
          readOnly
        />
      );
    case "file":
      return (
        <div
          style={{
            ...fieldStyle,
            padding: 20,
            textAlign: "center",
            borderStyle: "dashed",
            color: "var(--color-text-3)",
          }}
        >
          📁 Dosya sürükle-bırak veya seç
          <div style={{ fontSize: "0.7rem", marginTop: 4 }}>
            {o.acceptedMimeTypes.length > 0 ? o.acceptedMimeTypes.join(", ") : "Tüm formatlar"} ·
            max {o.maxSizeMb}MB
          </div>
        </div>
      );
    case "table":
      return (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr>
              {o.columns.map((c) => (
                <th
                  key={c.id}
                  style={{
                    border: "1px solid var(--color-border)",
                    padding: 6,
                    background: "var(--color-surface)",
                    textAlign: "left",
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {o.columns.map((c) => (
                <td key={c.id} style={{ border: "1px solid var(--color-border)", padding: 4 }}>
                  <input
                    type={c.type === "numeric" ? "number" : c.type === "date" ? "date" : "text"}
                    style={{
                      width: "100%",
                      padding: "4px 6px",
                      border: "none",
                      background: "transparent",
                      fontFamily: "var(--font-sans)",
                    }}
                    readOnly
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      );
    case "percent":
      return (
        <div style={{ display: "grid", gap: 6 }}>
          {o.categories.map((c) => (
            <div
              key={c.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px auto",
                gap: 8,
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "0.85rem" }}>{c.label}</span>
              <input type="number" style={fieldStyle} placeholder="0" readOnly />
              <span style={{ color: "var(--color-text-3)" }}>%</span>
            </div>
          ))}
          <div style={{ fontSize: "0.75rem", color: "var(--color-text-3)", marginTop: 4 }}>
            Toplam: 0% (hedef: 100%)
          </div>
        </div>
      );
    case "repeater":
      return (
        <div
          style={{
            padding: 10,
            border: "1px dashed var(--color-border)",
            borderRadius: 6,
          }}
        >
          <div style={{ fontSize: "0.75rem", color: "var(--color-text-3)", marginBottom: 8 }}>
            Tekrarlanabilir grup — her bloku için:
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {o.subFields.map((f) => (
              <div
                key={f.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: "0.8rem" }}>{f.label}</span>
                <input
                  type={f.type === "numeric" ? "number" : f.type === "date" ? "date" : "text"}
                  style={fieldStyle}
                  readOnly
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            disabled
            style={{
              marginTop: 8,
              padding: "4px 10px",
              border: "1px dashed var(--color-border)",
              borderRadius: 4,
              fontSize: "0.75rem",
              background: "transparent",
              color: "var(--color-text-3)",
            }}
          >
            + Bloku Ekle
          </button>
        </div>
      );

    case "composite":
      // M15 §4.4 — parça etiketleri + her parçanın kendi preview'ı, U1 düzeniyle
      // (etiket + ince çizgi). Derinlik 1 → recursion güvenli.
      return (
        <div>
          {o.parts.map((p, i) => (
            <div
              key={p.key}
              style={
                i > 0
                  ? { borderTop: "1px solid var(--color-border)", marginTop: 12, paddingTop: 12 }
                  : undefined
              }
            >
              <div
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: p.required ? "var(--color-text)" : "var(--color-text-2)",
                  marginBottom: 6,
                }}
              >
                {p.label}
              </div>
              {renderField(p.options)}
              {p.helperText && (
                <div
                  style={{
                    fontSize: "0.72rem",
                    fontStyle: "italic",
                    color: "var(--color-text-2)",
                    marginTop: 4,
                  }}
                >
                  {p.helperText}
                </div>
              )}
            </div>
          ))}
        </div>
      );
  }
}
